/**
 * MarketingFooter — public-facing footer per product-copy spec §5.2.
 *
 * Four columns: Product · Company · Resources · Legal.
 * Tagline under wordmark, copyright bottom-line.
 *
 * Iteration 46: when locale ≠ en-US, a "Help us improve {locale}" link
 * appears in the bottom line so reviewers can softly suggest better phrasing.
 *
 * Internal anchors point to existing routes; placeholder hashes (#) for routes
 * that don't exist yet (Help center, Status, Press) so the footer is visually
 * complete without dead 404s.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import Brand from "./Brand";
import ContactPanel from "./ContactPanel";
import TranslationFeedbackModal from "./TranslationFeedbackModal";
import { useI18n } from "../lib/i18n";
import { LOCALES } from "../lib/locales-config";

const COLUMNS = [
  {
    title: "Product",
    links: [
      { label: "For parents", href: "/#parents", testid: "footer-parents" },
      { label: "For staff", href: "/#staff", testid: "footer-staff" },
      { label: "For children", href: "/#children", testid: "footer-children" },
      { label: "For admin", href: "/#admin", testid: "footer-admin" },
      { label: "Privacy", href: "/privacy", testid: "footer-privacy-product" },
      { label: "API", href: "/#smart-building", testid: "footer-api" },
    ],
  },
  {
    title: "Company",
    links: [
      { label: "About", href: "/about", testid: "footer-about" },
      { label: "Careers", href: "#", testid: "footer-careers" },
      { label: "Contact", href: "/demo", testid: "footer-contact" },
      { label: "Press", href: "#", testid: "footer-press" },
    ],
  },
  {
    title: "Resources",
    links: [
      { label: "Help center", href: "#", testid: "footer-help" },
      { label: "Status", href: "#", testid: "footer-status" },
      { label: "Changelog", href: "#", testid: "footer-changelog" },
      { label: "Security", href: "/security", testid: "footer-security" },
    ],
  },
  {
    title: "Legal",
    links: [
      { label: "Privacy policy", href: "/privacy", testid: "footer-privacy" },
      { label: "Terms of service", href: "/terms", testid: "footer-terms" },
      { label: "Data processing addendum", href: "/dpa", testid: "footer-dpa" },
      { label: "Cookie preferences", href: "/cookies", testid: "footer-cookies" },
    ],
  },
];

function FooterLink({ label, href, testid }) {
  if (href.startsWith("/")) {
    return (
      <Link
        to={href}
        data-testid={testid}
        className="text-sm text-[#6B7280] hover:text-[#1A1B2E] transition-colors"
      >
        {label}
      </Link>
    );
  }
  return (
    <a
      href={href}
      data-testid={testid}
      className="text-sm text-[#6B7280] hover:text-[#1A1B2E] transition-colors"
    >
      {label}
    </a>
  );
}

export default function MarketingFooter() {
  const { t, locale } = useI18n();
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const showFeedbackLink = locale !== "en-US";
  const localeLabel = LOCALES[locale]?.autonym || locale;

  return (
    <footer
      data-testid="marketing-footer"
      className="border-t border-[#EEF0F4] bg-[#F7F8FC]"
    >
      <div className="mx-auto max-w-7xl px-6 sm:px-10 py-16">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-10">
          <div className="col-span-2 sm:col-span-3 lg:col-span-1">
            <Brand size="md" dataTestId="brand-marketing-footer" />
            <p className="mt-5 text-sm text-[#6B7280] leading-relaxed max-w-xs">
              Calm, clarity, and trust — in one beautiful platform.
            </p>
            <div className="mt-6">
              <ContactPanel variant="footer" />
            </div>
          </div>
          {COLUMNS.map((col) => (
            <div key={col.title}>
              <div className="eyebrow">{col.title}</div>
              <ul className="mt-5 space-y-3">
                {col.links.map((l) => (
                  <li key={l.label}>
                    <FooterLink {...l} />
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="mt-14 pt-7 border-t border-[#EEF0F4] flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <p className="text-xs text-[#6B7280]">
            © {new Date().getFullYear()} Alouvya. Built with care for children, staff,
            and families.
          </p>
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            {showFeedbackLink && (
              <button
                type="button"
                data-testid="footer-translation-feedback-trigger"
                onClick={() => setFeedbackOpen(true)}
                className="text-xs text-[#1F3A2A] hover:text-[#132C17] underline underline-offset-4 transition-colors text-left"
              >
                {t("marketing.translation_feedback.footer_link", { locale: localeLabel })}
              </button>
            )}
            <p className="text-xs text-[#9CA3AF]">
              Aligned with COPPA, GDPR, FERPA & PIPEDA.
            </p>
          </div>
        </div>
      </div>
      <TranslationFeedbackModal
        open={feedbackOpen}
        onClose={() => setFeedbackOpen(false)}
      />
    </footer>
  );
}
